import {
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { EntryService, MobileView } from '../../entry.service';
import { UtilService } from '../../../util.service';
import {
  SummaryText,
  SummaryData,
  TedDomain,
  ProviderData,
  PROVIDER_MAP,
} from '../../structure-data.models';
import {
  EntryData,
  PlddtData,
  Structure,
} from '../../../entry/structure-data.models';
import { GoogleAnalyticsService } from '../../../google-analytics.service';
import { CommonService } from '../../../common.service';
import { ConfigService } from '../../../config.service';
import { PlatformService } from '../../../platform.service';
import { MolstarPluginService } from '../../../molstar-plugin-service';
import {
  applyAlphaMissenseTooltips,
  colorByAlphaMissense,
  resetColoring,
  resetTooltips,
} from '../../../molstar-alpha-missense';
import { colorByTed } from '../../../molstar-alpha-ted';
import { catchError, filter, map, of, take } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { EntrySelectors } from 'src/app/state/entry.selectors';
import { Router } from '@angular/router';
export interface NavigationLink {
  id: string;
  title: string;
}
@Component({
  selector: 'overview-mobile',
  templateUrl: './overview-mobile.component.html',
  styleUrl: './overview-mobile.component.scss',
  standalone: false,
})
export class OverviewMobileComponent {
  // providerData: any;
  constructor(
    private store: Store,
    private entryService: EntryService,
    private utilService: UtilService,
    public commonService: CommonService,
    public gaService: GoogleAnalyticsService,
    private configService: ConfigService,
    private platform: PlatformService,
    private el: ElementRef,
    private molstarPluginService: MolstarPluginService,
    private router: Router
  ) {}

  public data: SummaryData;

  public pdbeMolstar: any;
  public accession = toSignal(this.store.select(EntrySelectors.accession));
  // public entryData = toSignal(this.store.select(EntrySelectors.entryData));
  public entryData = signal<EntryData>({} as EntryData);
  public complexData = signal<any>({});
  public complexDataList = signal<any[]>([]);
  public macromoleculesData = toSignal(this.store.select(EntrySelectors.macromoleculesData));

  // Mirrors SummaryTabComponent.summaryComponents — see that component for the full
  // rationale (AFPDBE-1435: complex API's complexComposition/uniprotAccession indices
  // misalign when 2 chains share an accession, and it has no residue-range field at all).
  // Shows the residue range whenever this component is a fragment of a larger UniProt
  // protein (start !== 1), not just when it collides with a same-accession sibling
  // (AFPDBE-1445 follow-up: one consistent rule wherever a UniProt accession is shown).
  public summaryComponents = computed(() => {
    const groups = this.utilService.groupMacromoleculeChains(this.macromoleculesData() || []);

    return groups.map((group) => {
      const representative = group[0];
      const accession = representative?.uniprotAccession || '';
      const range = this.utilService.getMacromoleculeResidueRange(representative);
      return {
        identifier: accession,
        stoichiometry: group.length,
        uniprotDescription: representative?.uniprotDescription,
        uniprotAccession: accession,
        range: range && range.start !== 1 ? range : null,
      };
    });
  });
  public alsoFoundInData = signal<any[]>([]);
  public uniprotData = signal<any>({});
  public isBiologicalFunctionLoading = signal<boolean>(false);
  public tedDomainData = toSignal(this.store.select(EntrySelectors.tedDomainData));
  public csvAmData = toSignal(this.store.select(EntrySelectors.csvAmData));
  public complexKBCount = 0;
  public clustermmseqData = toSignal(this.store.select(EntrySelectors.clustermmseqData));
  public clusterfoldseekData = toSignal(this.store.select(EntrySelectors.clusterfoldseekData));
  public providerData = signal<ProviderData>({} as ProviderData);
  public providerName:string = "";
  public isTedDomain = signal<boolean>(false);
  private readonly destroyRef = inject(DestroyRef);
  public isCopySequence: boolean = false;
  public uniprotBaseUrl = this.configService.getConfig().uniprotEntryUrl;

  public infoSectionHeight: any = { maxHeight: '1000px', overflow: 'hidden' };
  public showInfoSection = true;
  public kbCount = 0;
  public summaryTexts: any[][] = [];
  private summaryGroupSize = 15; // or 5, or any number

  public structure: Structure;
  public lastUpdateDate: string;
  public molLoadComplete: boolean = false;
  public isAlphaMis: boolean = false;
  public isModalConf: boolean = true;

  public isOldUniprotEntry: boolean = false;
  public proteinTitle: string = "";
  public curatedComplexId: string = '';

  public rangeFilter: any;
  public averageplddtDisplay:string;
  /* AlphaMissense Variables */
  public csvAMFileUrl: string;
  public hydrationReady: boolean = false;
  public plddtData: PlddtData;
  public isFullLinksDisplayed = signal<boolean>(false);
  public currentNavigationLink = signal<NavigationLink>({
    id: 'information',
    title: 'Information',
  });
  public readonly navigationLinks = [
    { id: 'information', title: 'Information' },
    { id: 'model-confidence-summary', title: 'Model Confidence Summary' },
    { id: 'audit-history', title: 'Audit history' },
    { id: 'similar-proteins', title: 'Similar Proteins' },
    { id: 'feedback', title: 'Feedback' },
    { id: 'help', title: 'Help, Last updated, and Licence' },
  ];
  
  ngOnInit(): void {

    this.store.select(EntrySelectors.state)
      .pipe(
        filter((state) => {
          const entryData = state?.entryData;
          const complexData = state?.complexData;

          if (!entryData || !entryData.modelEntityId) return false;

          if (entryData.isComplex) {
            if (!complexData || !complexData.modelEntityId) {
              return false;
            }

            return true;
          }

          if (complexData === undefined) {
            return false;
          }

          return true;
        }),
        take(1),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((state) => {
        const entryData = state.entryData;
        const complexData = state.complexData;
        const complexDataList = state.complexDataList;
        const uniprotData = state.uniprotData;
        const providerData = state.providerData;

        this.entryData.set(entryData)
        this.complexData.set(complexData)
        this.complexDataList.set(complexDataList || []);
        this.alsoFoundInData.set(complexDataList || []);
        this.uniprotData.set(uniprotData);
        this.isBiologicalFunctionLoading.set(this.shouldShowBiologicalFunctionLoader(uniprotData));
        this.providerData.set(providerData);

        this.createSummaryData();
        this.getPdbeData();
        if(this.entryData().isComplex){
          this.getCuratedComplexText();
        }
        if(entryData.isComplex) {
          this.proteinTitle = complexData?.complexName ?? "Not named";
        }else{
          this.proteinTitle = entryData?.uniprotDescription ?? "Not named";
        }
        this.providerName = PROVIDER_MAP[entryData.providerId];
      });

    this.store.select(EntrySelectors.uniprotData)
      .pipe(
        filter(() => !!this.entryData()?.modelEntityId),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((uniprotData) => {
        this.uniprotData.set(uniprotData);
        this.isBiologicalFunctionLoading.set(this.shouldShowBiologicalFunctionLoader(uniprotData));

        if (this.data && this.entryData().isComplex !== true) {
          this.uniprotSummaryData();
        }
      });
    
    if (this.platform.isBrowserPlatform()) {
      this.hydrationReady = true;
    }
  }

  private shouldShowBiologicalFunctionLoader(uniprotData: any): boolean {
    if (!this.entryData()?.isUniProt) {
      return false;
    }

    if (!uniprotData) {
      return true;
    }

    if (uniprotData.__status === 'not_found' || uniprotData.__status === 'error') {
      return false;
    }

    return !uniprotData.accession;
  }

  getCuratedComplexText() {
    const accessions = (this.complexData()?.uniprotAccession || []).filter(Boolean);

    if (!accessions.length) {
      this.curatedComplexId = '';
      return;
    }

    const proteins = accessions.join(',');
    const apiUrl = `${this.configService.getConfig().complexIntactApiUrl}find?proteinAcs=${proteins}`;

    this.commonService.getApiData(apiUrl).pipe(
      map((apiData) => {
        const matches = Array.isArray(apiData?.exactMatches) ? apiData.exactMatches : [];
        const firstMatch = matches.find((match) => !!match?.complexAc);
        return firstMatch?.complexAc || '';
      }),
      catchError((error) => {
        console.error('Error fetching curated complex data:', error);
        return of('');
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((complexAc: string) => {
      this.curatedComplexId = complexAc;
    });
  }

  getCuratedComplexPortalUrl(): string {
    const apiUrl = `${this.configService.getConfig().complexPortalUrl}`;
    return this.curatedComplexId ? `${apiUrl}${this.curatedComplexId}` : '';
  }

  getExperimentalStructuresText() {
     //Get structure count in PDBe-KB
    const apiUrl = `https://www.ebi.ac.uk/pdbe/search/pdb/select?group=true&group.field=pdb_id&group.ngroups=true&rows=0&omitHeader=true&json.nl=map&q=uniprot_accession:${this.complexData().uniprotAccession[0]}&wt=json`;
    this.commonService.getApiData(apiUrl).subscribe(apiData => {
      if(apiData && apiData.grouped && apiData.grouped.pdb_id) {
        this.complexKBCount = apiData.grouped.pdb_id.ngroups;
      }
    });
  }
  getipSAELevel(ipSAE: number): string {
    return ipSAE >= 0.8 ? 'Very high' :  
          ipSAE >= 0.7 ? 'Confident' : 
          ipSAE >= 0.6 ? 'Low' :       
          'Very low';                  
  }

  @HostListener('window:scroll', [])
  onScroll() {
    if (this.platform.isBrowserPlatform()) {
      this.navigationLinks.forEach((section) => {
        const element = document.getElementById(section.id);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            this.currentNavigationLink.set(section);
          }
        }
      });
    }
  }

  public navigateTo3DView(type): void {
    this.entryService.setMobileView(MobileView.Molstar);
    this.entryService.updateSelectedTabName(type);
    
    //for GA tag
    if(type === 'MConfidence'){
      this.gaService.eventEmitter('view_3D_button', 'mobile', 'tap', 'view_3D_annotations', "Taps on View 3D and annotations in mobile view");
    }
    if(type === 'SProteins'){
      this.gaService.eventEmitter('view_similar_proteins', 'mobile', 'tap', 'view_3D_similar_proteins', 'Taps on Discover similar proteins in mobile view');
    }
  }

  public toggleNavigationLinks(): void {
    this.isFullLinksDisplayed.update((value) => !value);
  }
  cmSelectionUpdate() {}
  public summaryGroup(arr: any[], size: number): any[][] {
    const result = [];
    for (let i = 0; i < arr.length; i += size) {
      result.push(arr.slice(i, i + size));
    }
    return result;
  }

  public onRelatedEntryClick(linkType: string): void {
    this.gaService.emitRelatedEntryClick(linkType, this.entryData()?.isComplex, this.entryData()?.assemblyType);
  }

  public getShortenedText(text: string): string {
    if (!text) {
      return;
    }
    const words = text.split(' ');
    if (words.length <= this.data.text_limit) {
      return text;
    }
    const wordSubset = words.slice(0, this.data.text_limit);
    return wordSubset.join(' ') + ' ...';
  }

  public scrollToSection(event: Event, sectionId: string): void {
    if (this.platform.isBrowserPlatform()) {
      event.preventDefault();
      this.isFullLinksDisplayed.set(false);
      const element = document.getElementById(sectionId);
      const toc = document.querySelector('.table-of-contents') as HTMLElement;
      if (element && toc) {
        const offsetTop = element.offsetTop;
        window.scrollTo({
          top: offsetTop - toc.offsetHeight,
          behavior: 'smooth',
        });
      }

      //for GA tags
      let tag = "";
      let gtagmsg = "";
      if(sectionId === "information"){
        tag = "information";
        gtagmsg = "Tap on Information anchor link in mobile";
      }
      if(sectionId === "model-confidence-summary"){
        tag = "model_confidence_summary";
        gtagmsg = "Tap on Model Confidence Summary anchor link in mobile";
      }
      if(sectionId === "similar-proteins"){
        tag = "similar_proteins";
        gtagmsg = "Tap on Similar proteins anchor link in mobile";
      }
      if(sectionId === "feedback"){
        tag = "feedback";
        gtagmsg = "Tap on Feedback anchor link in mobile";
      }
      if(sectionId === "help"){
        tag = "help";
        gtagmsg = "Tap on Helpanchor link in mobile";
      }
      this.gaService.eventEmitter('anchor_link_mobile', 'mobile_navigation', 'tap', tag, gtagmsg);
    }
  }
  
  public getPlddtLevel(plddt: number): string {
    return plddt > 90
      ? 'Very high'
      : plddt > 70
      ? 'High'
      : plddt > 50
      ? 'Low'
      : 'Very low';
  }
  
  getUniProtData(): void {
    this.commonService
      .getApiData(
        this.configService.getConfig().uniprotApiUrl + this.accession()
      )
      .pipe(
        map((uniProtData) => {
          this.uniprotData = uniProtData;
          return uniProtData;
        }),
        catchError((error) => {
          console.error('Error fetching UniProt data:', error);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((uniProtData) => {
        this.createSummaryData();
      });
  }

  getPdbeData() { 
    if(this.entryData().isUniProt === true){
      const accessionName = this.entryData().uniprotAccession;
   
      // this is the url which we ther from start
      // const solrApiUrl = `https://www.ebi.ac.uk/pdbe/search/pdb/select?group=true&group.field=pdb_id&group.ngroups=true&group.facet=true&facet=true&rows=0&facet.limit=5&facet.offset=0&facet.mincount=1&facet.sort=count&omitHeader=true&json.nl=map&facet.field=uniprot_accession&facet.threads=-1&q=uniprot_accession:${accessioName}&wt=json`;
       
      // below is the url shared by Sameer 
      const solrApiUrl = `https://www.ebi.ac.uk/pdbe/search/pdb/select?group=true&group.field=pdb_id&group.ngroups=true&rows=0&omitHeader=true&json.nl=map&q=uniprot_accession:${accessionName}&wt=json`;
  
      this.commonService.getApiData(solrApiUrl).pipe(
        map((apiData) => {
          if (apiData && apiData.grouped && apiData.grouped.pdb_id) {
            this.kbCount = apiData.grouped.pdb_id.ngroups; 
            this.uniprotSummaryData();
          }
        }),
        catchError((error) => {
          console.error('Error fetching pdbe data:', error);
          return of(null);
        }),
        takeUntilDestroyed(this.destroyRef),
  
      ).subscribe();
    }else{
      this.kbCount = 0;
      this.uniprotSummaryData();
    }
  }
  checkIfHasToShowMore(text: SummaryText): boolean {
    const words = text.text.split(' ');
    return !text.show_long && words.length >= this.data.text_limit;
  }
  switchText(text: SummaryText): boolean {
    text.show_long = !text.show_long;
    return text.show_long;
  }
  createSummaryData(): void {
    const dataItem = this.entryData();
    if (dataItem.globalMetricValue) {
      this.averageplddtDisplay = dataItem.globalMetricValue
        ? `${dataItem.globalMetricValue} (${this.getPlddtLevel(
            dataItem.globalMetricValue
          )})` : "";
    }

    // const tempOtherOrgamism = ["Tyrannosaurus rex","Arabidopsis thaliana","Herrania umbratica"];
    // this.otherOrganismScientificNames = {
    //   text:  dataItem.otherOrganismScientificNames ? dataItem.otherOrganismScientificNames.join(", ") : "", //tempOtherOrgamism.join(", "),
    //   totalCount: dataItem.otherOrganismScientificNames ? dataItem.otherOrganismScientificNames.length : 0,  //tempOtherOrgamism.length,
    //   italic: false,
    //   show_long: false,
    //   source: '',
    //   source_url: '',
    // }
    const summary = {
      title: '',
      subtitle: '',
      texts: [],
      text_limit: 20,
      flags: [],
      isUniProtReferenceProteome: false,
      isUniProtReviewed: false,
      afdb_accession: ''
    };

    summary.title = dataItem.uniprotDescription;
    summary.subtitle = 'AlphaFold structure prediction';
    summary.isUniProtReferenceProteome = dataItem.isUniProtReferenceProteome;
    summary.isUniProtReviewed = dataItem.isUniProtReviewed;
    summary.afdb_accession = `${dataItem.modelEntityId}-v${dataItem.latestVersion}`;

    if (dataItem.modelEntityId) {
      summary.texts.push({
        label: 'Protein',
        text: dataItem.uniprotDescription ? dataItem.uniprotDescription : 'Not named',
        italic: false,
        show_long: true,
        source: '',
        source_url: '',
      });
    }
    if (dataItem.modelEntityId) {
      const geneName = dataItem.gene ? dataItem.gene : 'Not available';
      summary.texts.push({
        label: 'Gene',
        text: geneName,
        italic: false,
        show_long: true,
        source: '',
        source_url: '',
      });
    }

    // Add common name after latin name, if available
    if (dataItem.organismScientificName) {
      summary.texts.push({
        label: 'Source organism',
        text: dataItem.organismScientificName,
        searchText: dataItem.organismScientificName,
        italic: true,
        show_long: true,
        source: '',
        source_url: '',
      });
    }
    if (dataItem.uniprotAccession) {
      // A fragment of a larger UniProt protein (residue numbering doesn't start at 1) —
      // surface that range next to the accession (AFPDBE-1445 follow-up).
      const uniprotRange = this.utilService.getMacromoleculeResidueRange(dataItem);
      const rangeSuffix = uniprotRange && uniprotRange.start !== 1 ? ` [residues: ${uniprotRange.start} – ${uniprotRange.end}]` : '';
      summary.texts.push({
        label: 'UniProt',
        text: dataItem.uniprotAccession + rangeSuffix,
        italic: false,
        show_long: true,
        source: 'UniProt',
        source_url:
          this.configService.getConfig().uniprotEntryUrl +
          dataItem.uniprotAccession,
      });
    }
    // if (dataItem.globalMetricValue) {
    //   const averagepLDDT = dataItem.globalMetricValue ? `${dataItem.globalMetricValue} (${this.getPlddtLevel(dataItem.globalMetricValue)})` : 'Not available';
    //   summary.texts.push({
    //     label: 'Average pLDDT',
    //     text: averagepLDDT,
    //     italic: false,
    //     show_long: true,
    //     source: 'tooltip',
    //     source_url: '',
    //   });
    // }
    if (dataItem.fractionPlddtVeryLow || dataItem.fractionPlddtLow || dataItem.fractionPlddtConfident || dataItem.fractionPlddtVeryHigh) {
      this.plddtData = {
        fractionPlddtVeryLow: this.entryData().fractionPlddtVeryLow,
        fractionPlddtLow: this.entryData().fractionPlddtLow,
        fractionPlddtConfident: this.entryData().fractionPlddtConfident,
        fractionPlddtVeryHigh: this.entryData().fractionPlddtVeryHigh
      };
    }
   
    this.data = summary;
    this.summaryTexts = this.summaryGroup(this.data.texts.slice(0, 8), this.summaryGroupSize);
  }

  uniprotSummaryData(): void {
    if (!this.data) {
      return;
    }

    const uniprotAccession = Array.isArray(this.entryData().uniprotAccession)
      ? this.entryData().uniprotAccession[0]
      : this.entryData().uniprotAccession;

    const matchingUniprotData =
      this.entryData().isUniProt &&
      this.uniprotData() &&
      this.uniprotData().accession === uniprotAccession
        ? this.uniprotData()
        : null;

    // Check if common name exists in UniProt data
    let commonName = '';
    if (
      matchingUniprotData &&
      matchingUniprotData.organism &&
      matchingUniprotData.organism.names
    ) {
      const names = matchingUniprotData.organism.names;
      const scientificName = names.find((name) => name.type === 'scientific')?.value || '';
      const shouldAppendCommonName =
        !!scientificName &&
        scientificName.trim().toLowerCase() ===
          (this.entryData().organismScientificName || '').trim().toLowerCase();

      names.forEach((name) => {
        if (name.type && name.type === 'common') {
          commonName = name.value;
        }
      });
      const sourceOrganism = this.data.texts.find(t => t.label === 'Source organism');
      if (sourceOrganism) {
        sourceOrganism.text = shouldAppendCommonName && commonName
          ? sourceOrganism.text + ' (' + commonName + ')'
          : sourceOrganism.text;
      }
    }

    // Default biological function text
    const bioFunction: SummaryText = {
      label: 'Biological function',
      text: 'Data unavailable',
      italic: false,
      show_long: false,
      source: 'tooltip',
      source_url: '',
      isTooltip: true
    };
    let catalyticActivity: string;
    // Set biological function and catalytic activity
    if (matchingUniprotData && matchingUniprotData.comments) {
      if (matchingUniprotData.comments[0].text) {
        bioFunction.text = matchingUniprotData.comments[0].text[0].value;
        bioFunction.text += '.';
        bioFunction.source = 'UniProt'
        bioFunction.source_url = "https://www.uniprot.org/uniprot/" + matchingUniprotData.accession;
      }
     
      matchingUniprotData.comments.forEach((comment) => {
        // if (comment.type === 'FUNCTION') {
        //   bioFunction.text = comment.text[0].value;
        // }
        if (comment.type === 'CATALYTIC_ACTIVITY') {
          catalyticActivity = comment.reaction.name;
        }
      });
    }
    
    if (bioFunction.text === 'Data unavailable' && catalyticActivity !== undefined) {
      bioFunction.text = 'Catalytic activity: ' + catalyticActivity;
    }
   
    if (bioFunction.text !== 'Data unavailable') {
      const pubmed = 'PubMed:([0-9]+)';
      const re = new RegExp(pubmed, 'g');
      bioFunction.text = bioFunction.text.replace(
        re,
        '<a href="https://www.uniprot.org/citations/$1" target="_blank">PubMed:$1</a>'
      );
    }
   
    const bioidx = this.data.texts.findIndex((o) => o.label === 'Biological function');
    if (bioidx !== -1) {
      this.data.texts.splice(bioidx, 1, bioFunction);
    } else {
      this.data.texts.splice(4, 0, bioFunction);
    }
    // Add KB data
    const kbText =
      this.kbCount === 0
        ? 'None available in the PDB'
        : `${this.kbCount} structure${this.kbCount > 1 ? 's' : ''} in PDB for ${
          uniprotAccession
        }`;
    const kbSource = this.kbCount === 0 ? '' : 'PDBe-KB';
    const kbUrl =
      this.kbCount === 0
        ? ''
        : this.configService.getConfig().pdbeKbEntryUrl + (matchingUniprotData?.accession || uniprotAccession);
    const kbdata = {
      label: 'Experimental structures',
      text: kbText,
      italic: false,
      show_long: true,
      source: kbSource,
      source_url: kbUrl,
    };
   
    const kbidx = this.data.texts.findIndex((o) => o.label === 'Experimental structures');

    if (kbidx !== -1) {
      // remove from old position
      this.data.texts.splice(kbidx, 1);
    }
    // insert at 6th position (index 5)
    this.data.texts.splice(5, 0, { ...kbdata });
   
    this.summaryTexts = this.summaryGroup(this.data.texts.slice(0, 8), this.summaryGroupSize);
  }

}
